import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import { memo, useCallback, useMemo } from "react";
import {
  TIntegrationRuleRelationshipGroupForm,
  integrationRuleRelationshipGroupSchema,
} from "./integrationRuleRelationshipGroupSchema";
import {
  AutoComplete,
  FormContainer,
  FormInput,
  Treturn,
  useFetch,
} from "@4uhub/lib4uhub";
import { IIntegrationRuleRelationshipGroupProps } from "./models";
import {
  createIntegrationRuleRelationshipGroup,
  getIntegrationRuleRelationshipGroupById,
  updateIntegrationRuleRelationshipGroupById,
} from "../../../../../../../services/integrationRuleRelationshipGroup.service";
import { uBusRelationshipGroupList } from "../../../../../../../services/uBusRelationshipGroup.service";

const translationPath =
  "page.4uBus.integrationRule.integrationRuleRelationshipGroup.";

export const IntegrationRuleRelationshipGroup = memo(
  ({
    id,
    busIntegrationRuleId,
    busAutomationCode,
    onBackButton,
  }: IIntegrationRuleRelationshipGroupProps) => {
    const { t } = useTranslation();

    const { sendRequest: save, loading: sLoading } = useFetch(
      createIntegrationRuleRelationshipGroup,
    );

    const { sendRequest: update, loading: uLoading } = useFetch(
      updateIntegrationRuleRelationshipGroupById,
    );

    const { sendRequest: get, loading: gLoading } = useFetch(
      getIntegrationRuleRelationshipGroupById,
    );

    const saveHandler = (v: TIntegrationRuleRelationshipGroupForm) => {
      const priority = Number(v.priority);
      return save({
        ...v,
        priority: !Number.isNaN(priority) ? priority : undefined,
        _4uBusIntegrationRuleId: busIntegrationRuleId ?? "",
        _4uBusRelationshipGroupId: v.uBusRelationshipGroup.id,
      });
    };

    const updateHandler = (
      v: TIntegrationRuleRelationshipGroupForm,
      id: string,
    ) => {
      const priority = Number(v.priority);
      return update({
        item: {
          ...v,
          priority: !Number.isNaN(priority) ? priority : undefined,
          _4uBusIntegrationRuleId: busIntegrationRuleId ?? "",
          _4uBusRelationshipGroupId: v.uBusRelationshipGroup.id,
        },
        id: id,
      });
    };

    const getHandler = useCallback(
      async (
        id: string,
      ): Promise<Treturn<TIntegrationRuleRelationshipGroupForm>> => {
        const { data, success } = await get(id);
        if (data && success) {
          const newData: TIntegrationRuleRelationshipGroupForm = {
            ...data,
            uBusRelationshipGroup: data._4uBusRelationshipGroup,
            priority: data.priority?.toString(),
          };
          return { data: newData, success: true };
        }

        return { data, success };
      },
      [get],
    );

    const loading = sLoading || uLoading || gLoading;

    const relationshipGroupParams = useMemo(
      () =>
        busAutomationCode
          ? {
              AutomationCode: busAutomationCode,
            }
          : undefined,
      [busAutomationCode],
    );

    return (
      <FormContainer<TIntegrationRuleRelationshipGroupForm>
        saveFunction={saveHandler}
        updateFunction={updateHandler}
        getFunction={getHandler}
        loading={loading}
        title={t(translationPath + "integration_rule_relationship_group")}
        schema={integrationRuleRelationshipGroupSchema}
        subtitle={t(
          translationPath + "new_integration_rule_relationship_group",
        )}
        backButtonOnClick={onBackButton}
        redirectOnSave={false}
        onSuccess={onBackButton}
        isModalForm={true}
        propId={id}
        formActionsOnBottom={false}
        fields={() => (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={8}>
              <AutoComplete
                params={relationshipGroupParams}
                request={uBusRelationshipGroupList}
                name="uBusRelationshipGroup"
                size="small"
                getOptionLabel={({ name }) => name}
                label={t(translationPath + "u_bus_relationship_group")}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormInput
                size="small"
                name="priority"
                fullWidth
                label={t(translationPath + "priority") || "Priority"}
              />
            </Grid>
          </Grid>
        )}
      />
    );
  },
);
